import React, { useRef } from "react";
import { motion, useInView, type Variants } from "framer-motion";

// ─── Animation helpers ────────────────────────────────────────────────
const fadeUp: Variants = {
  hidden: { opacity: 0, y: 32 },
  show: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1] as const,
      delay: i * 0.08,
    },
  }),
};

export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  show: (i = 0) => ({
    opacity: 1,
    transition: { duration: 0.6, ease: "easeOut", delay: i * 0.08 },
  }),
};

export function InView({
  children,
  className,
  delay = 0,
  margin = "-80px",
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  margin?: string;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: margin as any });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial="hidden"
      animate={inView ? "show" : "hidden"}
      custom={delay}
      variants={fadeUp}
    >
      {children}
    </motion.div>
  );
}

/** Pill / tag label shared across pages */
export function EyebrowLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-itl-blue mb-4">
      {children}
    </p>
  );
}

/** Dark hero band reused on every inner page */
export function PageHero({
  eyebrow,
  title,
  subtitle,
  image,
}: {
  eyebrow: string;
  title: React.ReactNode;
  subtitle?: string;
  image?: string;
}) {
  return (
    <div className="relative bg-itl-navy pt-40 pb-24 overflow-hidden">
      {image && (
        <>
          <img
            src={image}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-itl-navy/60 to-itl-navy" />
        </>
      )}
      {/* Decorative vertical rule */}
      <div className="absolute left-6 lg:left-12 top-0 bottom-0 flex items-center pointer-events-none">
        <div className="h-[35%] w-px bg-white/10" />
      </div>
      <div className="relative max-w-screen-xl mx-auto px-6 lg:px-12">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-[11px] font-semibold uppercase tracking-[0.25em] text-itl-blue mb-4"
        >
          {eyebrow}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
          className="font-serif text-5xl lg:text-6xl font-light text-white max-w-3xl leading-[1.08]"
        >
          {title}
        </motion.h1>
        {subtitle && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="text-white/55 text-base max-w-xl leading-relaxed mt-6"
          >
            {subtitle}
          </motion.p>
        )}
      </div>
    </div>
  );
}

/** Inline CTA strip shared across detail pages */
export function InlineCta({
  heading,
  body,
  ctaLabel,
  ctaHref,
}: {
  heading: string;
  body: string;
  ctaLabel: string;
  ctaHref: string;
}) {
  return (
    <section className="bg-itl-navy py-24">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-12 text-center">
        <InView>
          <EyebrowLabel>Ready to Start?</EyebrowLabel>
          <h2 className="font-serif text-4xl lg:text-5xl font-light text-white max-w-2xl mx-auto leading-snug mb-6">
            {heading}
          </h2>
          <p className="text-white/55 text-base max-w-lg mx-auto leading-relaxed mb-10">
            {body}
          </p>
          <a
            href={ctaHref}
            className="inline-block px-10 py-4 bg-itl-blue text-white text-[12px] font-semibold uppercase tracking-widest hover:bg-blue-600 transition-colors duration-200"
          >
            {ctaLabel}
          </a>
        </InView>
      </div>
    </section>
  );
}
